import React, { useCallback, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router';
import { memoizeFunction, Stack } from '@fluentui/react';
import { Open20Regular } from '@fluentui/react-icons';
import { ActionButton } from 'common/components/Buttons/v8';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { Heading3, Text } from 'common/components/Text';
import { useLocalizedStrings } from 'i18n';
import { useWebView } from 'store/webView/useWebView';

const getActionButtonStyles = memoizeFunction(() => ({
  label: {
    textAlign: 'start',
  },
  icon: {
    '& > svg': {
      paddingLeft: '0 !important',
    },
  },
}));

export const NativeNotificationSettings = () => {
  const { t } = useLocalizedStrings();
  const { openNativeNotificationSettings } = useWebView();
  const { search } = useLocation();
  const { isMobileView } = usePanelMobileView();

  const isNativeNotificationEnabled = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    const isNativeNotificationEnabledQueryValue = urlParams.get('isNativeNotificationEnabled');

    if (isNativeNotificationEnabledQueryValue?.toLowerCase() === 'true') {
      return true;
    }
    if (isNativeNotificationEnabledQueryValue?.toLowerCase() === 'false') {
      return false;
    }
    return null;
  }, [search]);

  const statusText = useMemo(() => {
    const components = {
      status: <Text variant="Body1Strong" />,
    };

    if (isNativeNotificationEnabled === true) {
      return (
        <Text variant="Body1">
          <Trans i18nKey="OfficeShellPanel.NotificationSettings.Sections.Native.StatusOn" components={components} t={t} />
        </Text>
      );
    }

    if (isNativeNotificationEnabled === false) {
      return (
        <Text variant="Body1">
          <Trans i18nKey="OfficeShellPanel.NotificationSettings.Sections.Native.StatusOff" components={components} t={t} />
        </Text>
      );
    }

    return undefined;
  }, [isNativeNotificationEnabled, t]);

  const onRenderIcon = useCallback(() => <Open20Regular />, []);

  return (
    <Stack tokens={{ childrenGap: 16, padding: isMobileView ? '0 24px' : undefined }}>
      <Heading3 variant="Title2Strong" block>
        {t('OfficeShellPanel.NotificationSettings.Sections.Native.Title')}
      </Heading3>
      <Stack tokens={{ childrenGap: 8 }}>
        {statusText}
        <Text variant="Caption2Strong" color="listHeaderText">
          {t('OfficeShellPanel.NotificationSettings.Sections.Native.Caption')}
        </Text>
      </Stack>
      <ActionButton
        ariaLabel={t('OfficeShellPanel.NotificationSettings.Sections.Native.OpenSettingsAriaLabel')}
        onRenderIcon={onRenderIcon}
        styles={getActionButtonStyles()}
        onClick={openNativeNotificationSettings}
      >
        {t('OfficeShellPanel.NotificationSettings.Sections.Native.OpenSettings')}
      </ActionButton>
    </Stack>
  );
};
